import { Badge, Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@sea-snag/ui';

import type { EquipmentItem, EquipmentScore } from '../lib/api-client';
import { bandLabel, bandToneClass, formatScoreDelta } from '../lib/equipment-score';

/** Anything the equipment UI can render — list rows and comparison rows both satisfy it. */
export interface EquipmentComparable {
  name: string;
  equipment: EquipmentItem[];
  equipmentScore: EquipmentScore | null;
  /** List screens have no "your boat", so this is optional there. */
  isOwnBoat?: boolean;
}

/**
 * The equipment index, where 100 is the median boat of the same hull type.
 * Deliberately not framed as a price: it informs a pricing decision, it does not
 * make one. See docs/specs/2026-09-01-equipment-score-design.md.
 */
export function EquipmentScoreBadge({
  boat,
  reference = null,
}: {
  boat: EquipmentComparable;
  /** Omit on screens with nothing to compare against — then no delta is shown. */
  reference?: EquipmentComparable | null;
}) {
  const score = boat.equipmentScore;

  if (!score) {
    return (
      <TooltipProvider delayDuration={300}>
        <Tooltip>
          <TooltipTrigger asChild>
            <span
              tabIndex={0}
              aria-label="No equipment data for this boat"
              className="ml-1 rounded text-[10px] text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              —
            </span>
          </TooltipTrigger>
          <TooltipContent>No equipment data for this boat</TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  // No delta on your own boats (comparing your boat to your boat is noise), and
  // none across hull groups — the two indices divide by different anchors, so the
  // difference has no meaning.
  const comparable =
    reference !== null &&
    !boat.isOwnBoat &&
    reference.equipmentScore?.hullGroup === score.hullGroup;
  const delta = comparable ? formatScoreDelta(score, reference.equipmentScore) : null;

  return (
    <TooltipProvider delayDuration={300}>
      <Tooltip>
        <TooltipTrigger asChild>
          <span
            tabIndex={0}
            aria-label={`Equipment index ${score.score}, ${bandLabel(score.band)}. 100 is a typical ${
              score.hullGroup === 'sail' ? 'sailing boat' : 'motor boat'
            }.`}
            className="ml-1 inline-flex items-center gap-1 rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <Badge className={`px-1 py-0 text-[10px] tabular-nums ${bandToneClass(score.band)}`}>
              {score.score}
            </Badge>
            {delta && (
              <span
                // A competitor ahead of you on equipment is a threat, not good news.
                // The hover card already reads "they have, you don't" in rose.
                className={`text-[10px] tabular-nums ${
                  delta === 'level'
                    ? 'text-muted-foreground'
                    : delta.startsWith('+')
                      ? 'text-rose-600 dark:text-rose-400'
                      : 'text-emerald-600 dark:text-emerald-400'
                }`}
              >
                {delta}
              </span>
            )}
          </span>
        </TooltipTrigger>
        <TooltipContent className="max-w-64">
          <p className="font-medium">
            {bandLabel(score.band)} · {score.score}
          </p>
          <p className="mt-1 text-xs">
            100 = a typical {score.hullGroup === 'sail' ? 'sailing boat' : 'motor boat'} in this
            market. The index weights each item by value, so it does not track the plain count: this
            boat carries {score.itemsCounted} of {score.itemsApplicable} items that apply to its
            type.
          </p>
          <p className="mt-1 text-xs text-muted-foreground">
            Equipment only — it does not account for age, condition or location.
          </p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
